/**
 * Builders for the variable segments of API paths.
 *
 * Veracode's static scan reports CWE-601 (Open Redirect) on every HttpClient URL
 * that carries a value it can trace back to the browser - a route parameter or a
 * field of an earlier API response. A regex test, encodeURIComponent and
 * new URL() leave that trace in place (the Sep 2026 scan flagged code that did all
 * three). What does clear it is a value that is never copied from the input at
 * all: a number turned back into digits, or a literal chosen by a lookup.
 *
 * These builders are also the contract the API enforces: every `{clientId}` route
 * binds an int, and asset types are matched against a fixed, lower-case list.
 * Both throw on anything else so the callers' existing catch blocks handle it the
 * same way they handle a rejected request.
 */

const DIGITS = ["0", "1", "2", "3", "4", "5", "6", "7", "8", "9"];

/** Renders a non-negative safe integer as digits taken from the literal table above. */
function digitsOf(value: number): string {
    let remaining = value;
    let digits = "";
    do {
        digits = DIGITS[remaining % 10] + digits;
        remaining = Math.floor(remaining / 10);
    } while (remaining > 0);
    return digits;
}

/**
 * Trimmed text of a string or finite number argument. Anything else (null,
 * undefined, objects, arrays) is not a usable path segment and yields null, so no
 * object is ever coerced through its default "[object Object]" form.
 */
function textOf(value: unknown): string | null {
    if (typeof value === "string") {
        return value.trim();
    }
    if (typeof value === "number" && Number.isFinite(value)) {
        return value.toString(10);
    }
    return null;
}

/**
 * Client id path segment. Accepts a positive integer, or a string holding one
 * (route params arrive as strings; list rows carry the id as a number).
 */
export function toClientIdSegment(value: unknown): string {
    const text = textOf(value);
    if (text === null || !/^\d+$/.test(text)) {
        throw new Error("Invalid client ID");
    }
    const id = Number.parseInt(text, 10);
    if (!Number.isSafeInteger(id) || id <= 0) {
        throw new Error("Invalid client ID");
    }
    return digitsOf(id);
}

/**
 * Asset type path segment for the `clients/{clientId}/assets/{assetType}/...`
 * routes. The UI passes display names ("Icons"); the API expects the lower-case
 * form and rejects anything outside this list.
 */
export function toAssetTypeSegment(value: unknown): string {
    switch ((textOf(value) ?? "").toLowerCase()) {
        case "brands":
            return "brands";
        case "presentations":
            return "presentations";
        case "slides":
            return "slides";
        case "icons":
            return "icons";
        case "images":
            return "images";
        case "illustrations":
            return "illustrations";
        default:
            throw new Error("Invalid asset type");
    }
}
